import SaveIcon from "@mui/icons-material/Save";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled, Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { getApiErrorMessage, isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import LoadingButton from "_shared/_views/loading/loading-button";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import MarketplaceTeam from "marketplace/teams/team";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import {
  useCreateTeamTemplateMutation,
  useRegisterTeamTemplateToMarketplaceMutation,
  useUpdateTeamTemplateMutation,
} from "work/team-templates/_api/team-template-api";
import { getTeamTemplateById } from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplate from "work/team-templates/team-template";
import EditVendorTeam from "work/work-agreements/teams/vendor-team/_views/vendor-team-edit-view";
import type { TeamPeopleChange } from "work/work-agreements/teams/people-picker/team-people-selection";
import { applyTeamPeopleChange, type TeamRosterValue } from "work/work-agreements/teams/people-picker/team-roster-change";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";

const Wrapper = styled("main")(({ theme }) => ({
  display: "grid",
  // `minmax(0, …)` rather than a bare `1fr`, whose floor is its content's minimum width: the roster
  // table's columns are wider than a phone, and they stretched this one column — and the Team Name
  // field with it — past the page's right edge. With a zero floor the column is the page's width and
  // the table scrolls inside it.
  gridTemplateColumns: "minmax(0, 1fr)",
  gridTemplateRows: "auto 1fr auto",
  paddingTop: theme.spacing(1),
  width: "100%",
}));
const HeadSection = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    gap: theme.spacing(1),
  },
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  gridColumnStart: 1,
  gridRowStart: 1,
  paddingTop: 0,
  width: "100%",
}));
const HeadInputFields = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  width: "100%",
}));
const TeamNameField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginRight: 0,
    width: "100%",
  },
  marginBottom: 0,
  marginRight: theme.spacing(1),
  maxWidth: theme.spacing(80),
  width: "50%",
}));
const TeamRoster = styled(EditVendorTeam)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));
const SaveButton = styled(LoadingButton)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    width: "fit-content",
  },
  width: "fit-content",
}));

const HTTP_BAD_REQUEST = 400;
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
// TODO(#4907): Remove this exact-text migration adapter after the team-template API migration
// subtask emits only the v1 Problem Details profile.
const knownLegacyTeamTemplateErrors = {
  "Duplicate team template": {
    code: "duplicate_team_template_name",
    title: "Team template already exists",
    detail: "Duplicate team template",
  },
  "Name is required": {
    code: "team_template_name_required",
    title: "Team template name is required",
    detail: "Name is required",
  },
  "You do not have permission to create a team template.": {
    code: "team_template_create_forbidden",
    title: "You do not have permission to create a team template",
    detail: "You do not have permission to create a team template.",
  },
} as const;

/**
 * Lives outside the component on purpose. The React Compiler cannot lower a `finally` clause, nor
 * optional chaining / `&&` / `??` inside a try-catch — and this routine needs all of them, so leaving
 * it inline made the compiler skip memoizing the whole form. Module-level functions are not compiled,
 * so the original control flow is preserved exactly.
 */
async function saveTeamTemplate(args: {
  teamTemplate: TeamTemplate | undefined;
  formSelectedTeamLeader: EntityMember | null;
  formSelectedTeamMembers: EntityMember[];
  formSelectedMarketplaceId: Guid | undefined;
  formTeamName: string;
  formRegisterToMarketplace: boolean;
  session: ReturnType<typeof useSession>;
  navigate: NavigateFunction;
  onSave?: (team: TeamTemplate) => void;
  setSaving: (saving: boolean) => void;
  createTeamTemplate: ReturnType<typeof useCreateTeamTemplateMutation>[0];
  updateTeamTemplate: ReturnType<typeof useUpdateTeamTemplateMutation>[0];
  registerTeamTemplateToMarketplace: ReturnType<typeof useRegisterTeamTemplateToMarketplaceMutation>[0];
}): Promise<void> {
  const {
    teamTemplate, formSelectedTeamLeader, formSelectedTeamMembers, formSelectedMarketplaceId,
    formTeamName, formRegisterToMarketplace, session, navigate, onSave, setSaving,
    createTeamTemplate, updateTeamTemplate, registerTeamTemplateToMarketplace,
  } = args;

  if (!formSelectedTeamLeader?.userId || !formSelectedTeamLeader.legalEntityId) {
    return;
  }

  try {
    setSaving(true);

    const newTeam = new VendorTeam({
      leader: formSelectedTeamLeader,
      members: formSelectedTeamMembers,
      marketplaceId: formSelectedMarketplaceId,
    });

    let returnedTemplate: TeamTemplate;
    if (teamTemplate?.id) {
      const response =
        await updateTeamTemplate({
          templateId: teamTemplate.id.value,
          originalTemplate: teamTemplate,
          updatedTeam: newTeam,
          updatedTeamName: new VendorTeamName(formTeamName),
        }).unwrap();
      returnedTemplate = TeamTemplate.fromJSON(response);
    }
    else {
      const response =
        await createTeamTemplate({
          entityId: session.user!.legalEntityId.value,
          team: newTeam,
          teamName: new VendorTeamName(formTeamName),
          creatorType: session.accountType,
        }).unwrap();
      returnedTemplate = TeamTemplate.fromJSON(response);
    }

    try {
      if (formRegisterToMarketplace && returnedTemplate.id && !teamTemplate?.marketplaceId) {
        const response =
          await registerTeamTemplateToMarketplace({
            templateId: returnedTemplate.id.value,
          }).unwrap();
        const responseTemplate = TeamTemplate.fromJSON(response);
        navigate(`/marketplace/profiles/teams/edit/${responseTemplate.marketplaceId?.value}`);
      }
    }
    catch (error) {
      console.error("Failed to register vendor team template to marketplace: " + error);
      enqueueSnackbar("Failed to register vendor team template to marketplace. Please try again.", {
        variant: "error",
      });
    }

    enqueueSnackbar("Saved vendor team template", { variant: "success" });
    onSave?.(returnedTemplate);
  } catch (error) {
    // 409 Conflict is for duplicate vendor-team names for calling user
    if (isFetchBaseQueryError(error) && error.status === HTTP_CONFLICT) {
      enqueueSnackbar(
        getApiErrorMessage(error.data, { knownLegacyText: knownLegacyTeamTemplateErrors })
          ?? "Team template already exists.",
        { variant: "warning" },
      );
    } else if (isFetchBaseQueryError(error) && error.status === HTTP_BAD_REQUEST) {
      enqueueSnackbar(
        getApiErrorMessage(error.data, { knownLegacyText: knownLegacyTeamTemplateErrors })
          ?? "Failed to save vendor team template.",
        { variant: "error" },
      );
    } else if (isFetchBaseQueryError(error) && error.status === HTTP_FORBIDDEN) {
      // A 403 is not something retrying fixes, so it must not be dressed as one. The API says which
      // grant is missing ("You do not have permission to create a team template."); telling the user
      // to try again instead sent them round the same loop and produced issue #4948.
      enqueueSnackbar(
        getApiErrorMessage(error.data, { knownLegacyText: knownLegacyTeamTemplateErrors })
          ?? "You do not have permission to save this team template.",
        { variant: "error" },
      );
    } else {
      console.error("Failed to save vendor team template: " + error);
      enqueueSnackbar("Failed to save vendor team template. Please try again.", {
        variant: "error",
      });
    }
  } finally {
    setSaving(false);
  }
}

type TeamTemplateFormProps = {
  templateId?: Guid;
  disableEditing?: boolean;
  navigate: NavigateFunction;
  onFormDirty: (dirty?: boolean) => void;
  onSave?: (team: TeamTemplate) => void;
  saveButtonContainer?: Element | null;
  saveButtonLabel?: string;
  onSelectionDrawerOpenChange?: (isOpen: boolean) => void;
};

export default function TeamTemplateForm(props: Readonly<TeamTemplateFormProps>) {
  const {
    templateId,
    navigate,
    onFormDirty,
    onSave,
    saveButtonContainer,
    onSelectionDrawerOpenChange,
  } = props;
  const disableEditing = props.disableEditing ?? false;
  const saveButtonLabel = props.saveButtonLabel ?? "Save Team";

  const teamTemplate = useSelector(getTeamTemplateById(templateId));

  const [formState, setFormState] = React.useState(() => ({
    sourceTemplate: teamTemplate,
    teamName: teamTemplate?.name?.value ?? "",
    selectedTeamLeader: teamTemplate?.leader ?? null,
    selectedTeamMembers: teamTemplate?.members ?? [],
    selectedMarketplaceId: teamTemplate?.marketplaceId,
    registerToMarketplace: teamTemplate?.marketplaceId !== undefined,
  }));

  const formTeamName = formState.sourceTemplate === teamTemplate ? formState.teamName : (teamTemplate?.name?.value ?? "");
  const formSelectedTeamLeader = formState.sourceTemplate === teamTemplate ? formState.selectedTeamLeader : (teamTemplate?.leader ?? null);
  const formSelectedTeamMembers = React.useMemo(
    () => formState.sourceTemplate === teamTemplate ? formState.selectedTeamMembers : (teamTemplate?.members ?? []),
    [formState.selectedTeamMembers, formState.sourceTemplate, teamTemplate],
  );
  const formSelectedMarketplaceId = formState.sourceTemplate === teamTemplate
    ? formState.selectedMarketplaceId
    : teamTemplate?.marketplaceId;
  const formRegisterToMarketplace = formState.sourceTemplate === teamTemplate
    ? formState.registerToMarketplace
    : teamTemplate?.marketplaceId !== undefined;

  const [saving, setSaving] = React.useState(false);
  const formValidated = formSelectedTeamLeader != null && formTeamName !== "";

  const session = useSession();
  const confirm = useConfirmDialog();
  const [createTeamTemplate] = useCreateTeamTemplateMutation();
  const [updateTeamTemplate] = useUpdateTeamTemplateMutation();
  const [registerTeamTemplateToMarketplace] = useRegisterTeamTemplateToMarketplaceMutation();

  const isLargeDownDisplay = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const isFormDirty = React.useMemo(() => {
    if (!teamTemplate) {
      return (
        formTeamName !== "" ||
        formSelectedTeamLeader !== null ||
        formSelectedTeamMembers.length > 0 ||
        formSelectedMarketplaceId !== undefined ||
        formRegisterToMarketplace
      );
    }

    const teamLeaderChanged = !teamTemplate.leader.isEqualTo(formSelectedTeamLeader);
    const teamMembersChanged =
      teamTemplate.members.length !== formSelectedTeamMembers.length ||
      teamTemplate.members.some((member, index) => !member.isEqualTo(formSelectedTeamMembers[index]));
    const marketplaceIdChanged = teamTemplate.marketplaceId?.isEqualTo(formSelectedMarketplaceId) === false ||
      (!teamTemplate.marketplaceId && formSelectedMarketplaceId !== undefined) ||
      (teamTemplate.marketplaceId !== undefined && !formSelectedMarketplaceId);
    const marketplaceRegistrationChanged = formRegisterToMarketplace !== (teamTemplate.marketplaceId !== undefined);

    return (
      formTeamName !== teamTemplate.name.value ||
      teamLeaderChanged ||
      teamMembersChanged ||
      marketplaceIdChanged ||
      marketplaceRegistrationChanged
    );
  }, [
    formRegisterToMarketplace,
    formSelectedMarketplaceId,
    formSelectedTeamLeader,
    formSelectedTeamMembers,
    formTeamName,
    teamTemplate,
  ]);

  React.useEffect(() => {
    onFormDirty(isFormDirty);
  }, [isFormDirty, onFormDirty]);


  /*
   * NOTE: In the future, it might be a good idea to implement a "getTeamTemplate" thunk in the team-templates-redux-slice
   * that requests a single team template from the server when we don't find it already in the store. We should also wire 
   * up loading/error states from the store and show loading feedback in the form while it's being fetched to eliminate 
   * the currently existing delay before the team roster component is populated when the form is shown immediately on load. 
   * This also would provide the opportunity to show an error to the user when the team template is not found OR have the 
   * server return 404 and respond to error. All this combined would allow the team-template-form to function as a standalone 
   * component that can be used anywhere in the app without the need for a parent component to pre-populate the team template 
   * into the store.
  useEffect(() => {
    if (teamTemplate || !templateId) return;

    dispatch(
      getTeamTemplate({
        session,
        templateId: templateId
      })
    ).unwrap().catch(() => {
      enqueueSnackbar("Team template could not be loaded.", { variant: "error" });
    });
  }, []);
  */


  function handleLeaderReplaced(leader: EntityMember | null) {
    setFormState((current) => ({
      ...current,
      sourceTemplate: teamTemplate,
      selectedTeamLeader: leader,
    }));
  }

  function replaceRoster(roster: TeamRosterValue) {
    setFormState((current) => ({
      ...current,
      sourceTemplate: teamTemplate,
      selectedTeamLeader: roster.leader,
      selectedTeamMembers: roster.members,
    }));
  }

  /** Everything the people picker chose, applied as one change to the roster this form rendered (#6602). */
  function handlePeopleAdded(change: TeamPeopleChange) {
    replaceRoster(
      applyTeamPeopleChange({ leader: formSelectedTeamLeader, members: formSelectedTeamMembers }, change),
    );
  }

  /** "Add All Colleagues": several members at once, which one-at-a-time adds would lose all but the last of. */
  function handleMembersAdded(members: EntityMember[]) {
    handlePeopleAdded({ members });
  }

  /** The Role cell's promotion: the member leads, and whoever led stays on as a member. */
  function handleMemberPromotedToLeader(member: EntityMember) {
    handlePeopleAdded({ leader: member, members: [], formerLeaderStays: true });
  }

  function handleMemberRemoved(userId?: Guid) {
    if (userId) {
      setFormState((current) => ({
        ...current,
        sourceTemplate: teamTemplate,
        selectedTeamMembers: formSelectedTeamMembers.filter((m) => !m.userId?.isEqualTo(userId)),
      }));
    } else {
      setFormState((current) => ({
        ...current,
        sourceTemplate: teamTemplate,
        selectedTeamMembers: [],
      }));
    }
  }

  async function handleMarketplaceTeamSelected(team: MarketplaceTeam) {
    if (formSelectedTeamLeader !== null || formSelectedTeamMembers.length > 0) {
      const response = await confirm({
        title: "Replace Team",
        message: "Are you sure you want to replace the current vendor team?",
        okButtonText: "Replace",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    if (!team.leader.profile?.firstName || !team.leader.profile?.lastName) {
      throw new Error("Team leader profile is undefined");
    }

    setFormState((current) => ({
      ...current,
      sourceTemplate: teamTemplate,
      selectedMarketplaceId: team.id,
      selectedTeamLeader: team.leader.toEntityMember(),
      selectedTeamMembers: team.members.map((member) => member.toEntityMember()),
      teamName: team.profile.name,
    }));

  }

  async function handleSaveTeamClick() {
    await saveTeamTemplate({
      teamTemplate, formSelectedTeamLeader, formSelectedTeamMembers, formSelectedMarketplaceId,
      formTeamName, formRegisterToMarketplace, session, navigate, onSave, setSaving,
      createTeamTemplate, updateTeamTemplate, registerTeamTemplateToMarketplace,
    });
  }

  function isCurrentTeamLeader(): boolean {
    return session.user?.id?.isEqualTo(formSelectedTeamLeader?.userId) ?? false;
  }

  const saveButton = (
    <SaveButton
      color="primary"
      variant="contained"
      disabled={!formValidated || !isFormDirty || saving}
      size="large"
      loading={saving}
      startIcon={<SaveIcon />}
      onClick={handleSaveTeamClick}
    >
      {saveButtonLabel}
    </SaveButton>
  );

  return (
    <Wrapper>
      {!disableEditing && (
        <HeadSection>
          <HeadInputFields>
            <TeamNameField
              value={formTeamName}
              onChange={(event) => {
                setFormState((current) => ({
                  ...current,
                  sourceTemplate: teamTemplate,
                  teamName: event.target.value,
                }));
              }}
              label="Team Name"
              required
            />
            {session.accountType === AccountType.Vendor && (
              <Tooltip
                title={(function () {
                  if (!isCurrentTeamLeader())
                    return "Only the vendor team leader can register the vendor team on the marketplace";
                  if (teamTemplate?.marketplaceId !== undefined) return "Team is already registered on the marketplace";
                  return "";
                })()}
              >
                <span>
                  <FormControlLabel
                    label="Register Team On Marketplace"
                    labelPlacement={isLargeDownDisplay ? "end" : "start"}
                    control={
                      <Checkbox
                        color="primary"
                        checked={formRegisterToMarketplace}
                        disabled={teamTemplate?.marketplaceId !== undefined || !isCurrentTeamLeader()}
                        onChange={(event) => {
                          setFormState((current) => ({
                            ...current,
                            sourceTemplate: teamTemplate,
                            registerToMarketplace: event.target.checked,
                          }));
                        }}
                      />
                    }
                  />
                </span>
              </Tooltip>
            )}
          </HeadInputFields>
        </HeadSection>
      )}
      <TeamRoster
        leader={formSelectedTeamLeader}
        members={formSelectedTeamMembers}
        disableEditing={disableEditing}
        hideFieldHistory={true}
        selectionPresentation="drawer"
        onLeaderUpdated={handleLeaderReplaced}
        onMembersAdded={handleMembersAdded}
        onMemberPromotedToLeader={handleMemberPromotedToLeader}
        onPeopleAdded={handlePeopleAdded}
        onMemberRemoved={handleMemberRemoved}
        onMarketplaceTeamSelected={handleMarketplaceTeamSelected}
        onSelectionDrawerOpenChange={onSelectionDrawerOpenChange}
      />
      {!disableEditing && (saveButtonContainer ? createPortal(saveButton, saveButtonContainer) : saveButton)}
    </Wrapper>
  );
}
